import React from 'react';
import { Microscope, BookOpenCheck, Network, GraduationCap, ChartNoAxesColumn } from 'lucide-react';

const pilaresSNII = [
  {
    icon: Microscope,
    title: 'Validación por Pares en Tiempo Real',
    color: 'text-[#0b742f]',
    bgColor: 'bg-[#0b742f]/10',
    borderColor: 'border-[#0b742f]/20',
    points: [
      'Análisis crítico: Permite que los avances preliminares de una investigación sean sometidos al escrutinio formal de otros expertos.',
      'Filtro de calidad: El SNII valora cualitativamente la revisión por pares. Un trabajo presentado en un congreso con arbitraje estricto demuestra rigor metodológico antes de convertirse en un artículo definitivo.'
    ]
  },
  {
    icon: BookOpenCheck,
    title: 'Acceso Universal al Conocimiento y Divulgación',
    color: 'text-[#e87729]',
    bgColor: 'bg-[#e87729]/10',
    borderColor: 'border-[#e87729]/20',
    points: [
      'Apropiación social: Los lineamientos del SNII exigen que la ciencia no se quede en las universidades.',
      'Retribución social: Las ponencias, conferencias magistrales y simposios son considerados plataformas de comunicación pública de la ciencia, tecnología e innovación. Esto cumple directamente con el eje obligatorio de Acceso Universal al Conocimiento.'
    ]
  },
  {
    icon: Network,
    title: 'Fortalecimiento de Redes y Vinculación (Incidencia)',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
    points: [
      'Proyectos multidisciplinarios: Los congresos son el semillero oficial para conformar redes de investigación interinstitucionales.',
      'Solución de problemas nacionales: El SNII premia la colaboración científica orientada a resolver problemáticas socioambientales o de innovación tecnológica. Estos lazos de cooperación técnica casi siempre se originan en mesas de debate de congresos.'
    ]
  },
  {
    icon: GraduationCap,
    title: 'Formación de Nuevos Talentos (Comunidad)',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-100',
    points: [
      'Fomento al posgrado: Cuando un investigador consolidado asiste a un congreso acompañado de sus estudiantes (coautores de carteles o ponencias), está cumpliendo con el rubro de Fortalecimiento de la Comunidad Científica.',
      'Dirección de proyectos: Demuestra activamente la mentoría y la inserción de nuevos cuadros de investigadores en circuitos internacionales de discusión.'
    ]
  }
];

const impactosSNII = [
  'Producción Científica (a través de memorias con registro ISSN/ISBN o DOI).',
  'Vinculación Especializada (cooperación científica nacional e internacional).',
  'Apropiación Social (comunicación de resultados a la comunidad).'
];

const SNIICongressInfoSection = ({ className = 'mb-12' }) => {
  return (
    <section className={className}>
      <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
        <div className="p-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
              <ChartNoAxesColumn className="w-8 h-8 text-[#0b742f]" />
              Participación en Congresos y Evaluación SNII
            </h2>
            <div className="h-1 w-32 bg-[#e87729] rounded ml-11"></div>
          </div>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-justify">
              Para el Sistema Nacional de Investigadoras e Investigadores (SNII), la participación de la comunidad científica en congresos no es un mero acto protocolario, sino un mecanismo de validación, vinculación y democratización del conocimiento regulado por las directrices de la SECIHTI. Bajo el enfoque actual de evaluación, Conahcyt utiliza estos foros académicos como indicadores clave por las siguientes razones fundamentales:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pilaresSNII.map((pilar, index) => {
                const Icon = pilar.icon;

                return (
                  <div
                    key={pilar.title}
                    className={`rounded-2xl border ${pilar.borderColor} p-6 shadow-sm hover:shadow-md transition-shadow duration-300`}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`${pilar.bgColor} p-3 rounded-xl`}>
                        <Icon className={`w-6 h-6 ${pilar.color}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-500 mb-1">{index + 1}</p>
                        <h3 className="text-xl font-bold text-gray-900">{pilar.title}</h3>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {pilar.points.map((point) => {
                        const [label, ...rest] = point.split(': ');

                        return (
                          <div key={point} className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-[#0b742f] flex-shrink-0"></span>
                            <p className="text-gray-700">
                              <strong>{label}:</strong> {rest.join(': ')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-gradient-to-r from-[#0b742f]/10 via-white to-[#e87729]/10 rounded-2xl border border-[#0b742f]/10 p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Impacto Técnico para la Evaluación</h3>
              <p className="text-gray-700 mb-4 text-justify">
                El SNII ya no evalúa solo contando "artículos publicados"; califica una trayectoria global con incidencia. En la plataforma del CVU, participar en congresos robustece simultáneamente tres grandes componentes obligatorios:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {impactosSNII.map((impacto, index) => (
                  <div key={impacto} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm font-semibold text-[#e87729] mb-2">{index + 1}</p>
                    <p className="text-gray-700">{impacto}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SNIICongressInfoSection;